import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Drawer,
  Icon,
  Input,
  Modal,
  Popconfirm,
  Space,
  Spin,
  Table,
  Tag,
  Text,
  useToast,
} from "@app/ui";
import { ErrorAlert, useErrorText } from "../errors/index.js";
import {
  createWebhook,
  listWebhookDeliveries,
  listWebhooks,
  retryWebhookDelivery,
  testWebhook,
  updateWebhook,
} from "../api/dpp.js";
import { useT, useFormatDateTime } from "../i18n.js";
import {
  WEBHOOK_EVENTS,
  type Webhook,
  type WebhookDelivery,
  type WebhookDeliveryStatus,
  type WebhookEvent,
} from "../types.js";

/**
 * "Webhooks" card of the organization settings page (machine integration for
 * ERP connectors). Create modal with event checkboxes, one-time signing-secret
 * display, enable/disable, test-fire, and a deliveries drawer with manual
 * retry for failed deliveries. Rendered only for holders of
 * `integration:manage` (admin) — mirrors ApiKeysCard.
 *
 * The signing secret exists ONLY in the create response — the API stores it
 * encrypted and never returns it again.
 */

const DELIVERY_COLOR: Record<WebhookDeliveryStatus, "default" | "success" | "error"> = {
  pending: "default",
  delivered: "success",
  failed: "error",
};

/** Deliveries beyond this many attempts can no longer be retried (server cap). */
const MAX_ATTEMPTS = 3;

export function WebhooksCard({ orgId }: { orgId: string }) {
  const t = useT();
  const fmtDate = useFormatDateTime();
  const toast = useToast();
  const errorText = useErrorText();

  const [hooks, setHooks] = useState<Webhook[] | null>(null);
  const [error, setError] = useState<unknown>(null);

  // Create-webhook modal
  const [createOpen, setCreateOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<WebhookEvent[]>(["passport.published"]);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<unknown>(null);
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);

  // Deliveries drawer
  const [deliveriesFor, setDeliveriesFor] = useState<Webhook | null>(null);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[] | null>(null);
  const [deliveriesError, setDeliveriesError] = useState<unknown>(null);

  const load = async () => {
    setError(null);
    try {
      setHooks(await listWebhooks(orgId));
    } catch (e) {
      setError(e);
    }
  };

  useEffect(() => {
    void load();
  }, [orgId]);

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t("webhooks.copied"));
    } catch {
      toast.error(t("error.actionFailed"));
    }
  };

  const openCreate = () => {
    setUrl("");
    setEvents(["passport.published"]);
    setCreateError(null);
    setCreatedSecret(null);
    setCreateOpen(true);
  };

  const toggleEvent = (event: WebhookEvent, checked: boolean) => {
    setEvents((prev) => (checked ? [...new Set([...prev, event])] : prev.filter((e) => e !== event)));
  };

  const submitCreate = async () => {
    if (url.trim() === "") {
      setCreateError(t("webhooks.urlRequired"));
      return;
    }
    if (events.length === 0) {
      setCreateError(t("webhooks.eventRequired"));
      return;
    }
    setCreateError(null);
    setCreating(true);
    try {
      const result = await createWebhook(orgId, { url: url.trim(), events });
      // The signing secret is only ever available HERE — show it once.
      setCreatedSecret(result.secret);
      toast.success(t("webhooks.created"));
      await load();
    } catch (e) {
      setCreateError(e);
    } finally {
      setCreating(false);
    }
  };

  const setStatus = async (hook: Webhook, status: "active" | "disabled") => {
    try {
      await updateWebhook(orgId, hook.id, { status });
      toast.success(status === "active" ? t("webhooks.enabled") : t("webhooks.disabled"));
      await load();
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  const fireTest = async (hook: Webhook) => {
    try {
      const delivery = await testWebhook(orgId, hook.id);
      if (delivery.status === "delivered") toast.success(t("webhooks.testOk"));
      else toast.error(`${t("webhooks.testFailed")}${delivery.lastError ? `: ${delivery.lastError}` : ""}`);
      await load();
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  const loadDeliveries = async (hook: Webhook) => {
    setDeliveriesError(null);
    try {
      const page = await listWebhookDeliveries(orgId, hook.id, { limit: 50 });
      setDeliveries(page.items);
    } catch (e) {
      setDeliveriesError(e);
    }
  };

  const openDeliveries = (hook: Webhook) => {
    setDeliveries(null);
    setDeliveriesFor(hook);
    void loadDeliveries(hook);
  };

  const retry = async (delivery: WebhookDelivery) => {
    if (!deliveriesFor) return;
    try {
      const updated = await retryWebhookDelivery(orgId, deliveriesFor.id, delivery.id);
      if (updated.status === "delivered") toast.success(t("webhooks.retryOk"));
      else toast.error(`${t("webhooks.retryFailed")}${updated.lastError ? `: ${updated.lastError}` : ""}`);
      await loadDeliveries(deliveriesFor);
      await load();
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  return (
    <Card
      title={t("webhooks.title")}
      extra={
        <Button size="sm" icon={<Icon name="plus" size={14} />} onClick={openCreate}>
          {t("webhooks.create")}
        </Button>
      }
    >
      {error != null && <ErrorAlert error={error} />}
      {hooks === null ? (
        <Spin />
      ) : (
        <Table
          rowKey={(w) => w.id}
          empty={t("webhooks.empty")}
          columns={[
            {
              key: "url",
              title: t("webhooks.url"),
              render: (w: Webhook) => (
                <Text code copyable>
                  {w.url}
                </Text>
              ),
            },
            {
              key: "events",
              title: t("webhooks.events"),
              render: (w: Webhook) => (
                <Space size="sm">
                  {w.events.map((e) => (
                    <Tag key={e}>{e}</Tag>
                  ))}
                </Space>
              ),
            },
            {
              key: "status",
              title: t("common.status"),
              render: (w: Webhook) => (
                <Tag color={w.status === "active" ? "success" : "default"}>
                  {t(`webhookStatus.${w.status}`)}
                </Tag>
              ),
            },
            {
              key: "lastDelivery",
              title: t("webhooks.lastDelivery"),
              render: (w: Webhook) =>
                w.lastDeliveryAt ? (
                  <Space size="sm">
                    <Tag color={w.lastDeliveryStatus === "delivered" ? "success" : "error"}>
                      {w.lastDeliveryStatus}
                    </Tag>
                    {fmtDate(w.lastDeliveryAt)}
                  </Space>
                ) : (
                  "—"
                ),
            },
            {
              key: "actions",
              title: t("common.actions"),
              render: (w: Webhook) => (
                <Space size="sm">
                  <Button size="sm" variant="subtle" onClick={() => void fireTest(w)}>
                    {t("webhooks.test")}
                  </Button>
                  <Button size="sm" variant="subtle" onClick={() => openDeliveries(w)}>
                    {t("webhooks.deliveries")}
                  </Button>
                  {w.status === "active" ? (
                    <Popconfirm
                      title={t("webhooks.disableConfirm")}
                      description={t("webhooks.disableDescription")}
                      okType="danger"
                      okText={t("webhooks.disable")}
                      cancelText={t("common.cancel")}
                      onConfirm={() => void setStatus(w, "disabled")}
                    >
                      <Button size="sm" variant="danger">
                        {t("webhooks.disable")}
                      </Button>
                    </Popconfirm>
                  ) : (
                    <Button size="sm" onClick={() => void setStatus(w, "active")}>
                      {t("webhooks.enable")}
                    </Button>
                  )}
                </Space>
              ),
            },
          ]}
          data={hooks}
        />
      )}

      {/* Create webhook */}
      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={createdSecret ? t("webhooks.secretTitle") : t("webhooks.createTitle")}
        footer={createdSecret ? null : undefined}
        okText={t("common.create")}
        cancelText={t("common.cancel")}
        onOk={() => void submitCreate()}
        confirmLoading={creating}
      >
        {createdSecret ? (
          <Space direction="vertical" size="md" style={{ width: "100%" }}>
            <Alert type="warning">{t("webhooks.secretOnce")}</Alert>
            <Input readOnly value={createdSecret} onFocus={(e) => e.target.select()} />
            <Space size="sm">
              <Button
                variant="primary"
                icon={<Icon name="copy" size={14} />}
                onClick={() => void copy(createdSecret)}
              >
                {t("webhooks.copy")}
              </Button>
              <Button onClick={() => setCreateOpen(false)}>{t("common.cancel")}</Button>
            </Space>
          </Space>
        ) : (
          <Space direction="vertical" size="md" style={{ width: "100%" }}>
            {createError != null && <ErrorAlert error={createError} />}
            <Text small secondary>
              {t("webhooks.intro")}
            </Text>
            <Input
              label={t("webhooks.url")}
              help={t("webhooks.urlHelp")}
              required
              placeholder="https://erp.example.com/dpp-events"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
            <Space direction="vertical" size="sm">
              <Text small>{t("webhooks.events")}</Text>
              {WEBHOOK_EVENTS.map((event) => (
                <Checkbox
                  key={event}
                  checked={events.includes(event)}
                  onChange={(e) => toggleEvent(event, e.target.checked)}
                >
                  <Text code>{event}</Text>
                </Checkbox>
              ))}
            </Space>
          </Space>
        )}
      </Modal>

      {/* Deliveries drawer */}
      <Drawer
        open={deliveriesFor !== null}
        onClose={() => setDeliveriesFor(null)}
        title={t("webhooks.deliveriesTitle")}
        width={640}
      >
        {deliveriesError != null && <ErrorAlert error={deliveriesError} />}
        {deliveries === null ? (
          <Spin />
        ) : (
          <Table
            rowKey={(d) => d.id}
            empty={t("webhooks.noDeliveries")}
            columns={[
              {
                key: "event",
                title: t("webhooks.event"),
                render: (d: WebhookDelivery) => <Text code>{d.event}</Text>,
              },
              {
                key: "status",
                title: t("common.status"),
                render: (d: WebhookDelivery) => (
                  <Tag color={DELIVERY_COLOR[d.status]}>{t(`deliveryStatus.${d.status}`)}</Tag>
                ),
              },
              {
                key: "attempts",
                title: t("webhooks.attempts"),
                render: (d: WebhookDelivery) =>
                  `${d.attempts}${d.responseStatus ? ` (${d.responseStatus})` : ""}`,
              },
              {
                key: "createdAt",
                title: t("common.createdAt"),
                render: (d: WebhookDelivery) => fmtDate(d.createdAt),
              },
              {
                key: "error",
                title: t("webhooks.lastError"),
                render: (d: WebhookDelivery) => (d.lastError ? <Text small>{d.lastError}</Text> : "—"),
              },
              {
                key: "actions",
                title: "",
                render: (d: WebhookDelivery) =>
                  d.status === "failed" && d.attempts < MAX_ATTEMPTS ? (
                    <Button size="sm" onClick={() => void retry(d)}>
                      {t("webhooks.retry")}
                    </Button>
                  ) : null,
              },
            ]}
            data={deliveries}
          />
        )}
      </Drawer>
    </Card>
  );
}
